import React, { useEffect, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentTypeSelector } from './PaymentTypeSelector';
import { StandardPayment } from './StandardPayment';
import { InstallmentPayment, Installment, Recipient, Supplier } from './InstallmentPayment';
import { CardOnFilePayment } from './CardOnFilePayment';
import { PreAuthPayment } from './PreAuthPayment';
import { ShopProductsPayment } from './ShopProductsPayment';
import { FixedLinksPayment } from './FixedLinksPayment';
import { BulkPaymentUpload } from './BulkPaymentUpload';
import { BulkPaymentListSimplified } from './BulkPaymentListSimplified';
import { RecurringPayment } from './RecurringPayment';
import { InfoIcon, CheckCircleIcon, CalendarIcon, ArrowLeftIcon, ListIcon, RepeatIcon } from 'lucide-react';
import { ShareLinkModal } from './ShareLinkModal';
import { SuccessModal } from '../../../shared/ui/SuccessModal';
export type PaymentType = 'standard' | 'installment' | 'card-on-file' | 'preauth' | 'shop-products' | 'fixed-links' | 'bulk' | 'recurring';
export type SubscriptionMode = 'existing' | 'new';
export function PaymentForm() {
  const {
    t
  } = useTranslation('payments');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Map URL tab parameter to payment type
  const getInitialPaymentType = (): PaymentType => {
    const tab = searchParams.get('tab');
    const tabMapping: Record<string, PaymentType> = {
      single: 'standard',
      bulk: 'bulk',
      split: 'installment',
      'fixed-links': 'fixed-links',
      charge: 'card-on-file',
      'security-hold': 'preauth',
      shop: 'shop-products',
      recurring: 'recurring'
    };
    return tabMapping[tab || ''] || 'standard';
  };
  const [paymentType, setPaymentType] = useState<PaymentType>(getInitialPaymentType());
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState<SubscriptionMode>('existing');
  const [installmentData, setInstallmentData] = useState<Installment[]>([]);
  const [isMultipleRecipients, setIsMultipleRecipients] = useState(false);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission' | null>(null);
  const [supplierData, setSupplierData] = useState<Supplier[]>([]);
  const [commissionTotalAmount, setCommissionTotalAmount] = useState('');
  const [commissionType, setCommissionType] = useState<'percentage' | 'fixed'>('percentage');
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [securityType, setSecurityType] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [currentPage, setCurrentPage] = useState(1);
  const batchesPerPage = 10;
  // Update payment type when URL parameter changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const newPaymentType = getInitialPaymentType();
      setPaymentType(newPaymentType);
    }
  }, [searchParams]);
  // Calculate days between now and expiry date
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => parseInt(part, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference
      const timeDiff = expiryDateObj.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setExpiryDays(daysDiff);
    }
  }, [expiryDate]);
  // This is a mock function that would normally get data from the InstallmentPayment component
  // In a real app, you would use a ref or state lifting to get the actual data
  const getInstallmentData = () => {
    // For demonstration purposes, we're returning mock data
    if (paymentType === 'installment') {
      return [{
        id: 1,
        amount: '100.00',
        description: 'First payment',
        dueDate: '04/15/2026',
        sendLinkDate: '04/10/2026',
        recipient: {
          id: 1,
          email: 'recipient1@example.com',
          phone: '+1 (555) 123-4567'
        }
      }, {
        id: 2,
        amount: '200.00',
        description: 'Second payment',
        dueDate: '05/15/2026',
        sendLinkDate: '05/10/2026',
        recipient: {
          id: 2,
          email: 'recipient2@example.com',
          phone: '+1 (555) 987-6543'
        }
      }];
    }
    return [];
  };
  // Callbacks from InstallmentPayment to track real state
  const handleSplitTypeChange = (type: 'dates' | 'recipients' | 'commission') => {
    setSplitType(type);
  };
  const handleInstallmentsChange = (data: Installment[]) => {
    setInstallmentData(data);
  };
  const handleSuppliersChange = (data: Supplier[]) => {
    setSupplierData(data);
  };
  const handleBeneficiaryTypeChange = (type: 'percentage' | 'fixed') => {
    setCommissionType(type);
  };
  const handleTotalPaymentAmountChange = (amount: string) => {
    setCommissionTotalAmount(amount);
  };
  const handleButtonClick = () => {
    if (paymentType === 'card-on-file') {
      // Show success confirmation for card on file
      setShowSuccessModal(true);
    } else if (paymentType === 'bulk') {
      // Navigate to bulk payments page using React Router
      navigate('/bulk-payments');
    } else if (paymentType === 'recurring' && subscriptionMode === 'existing') {
      // Show success confirmation for existing customer subscription
      setShowSuccessModal(true);
    } else {
      // Get data based on payment type
      if (paymentType === 'installment') {
        // splitType, installmentData, supplierData, commissionType, commissionTotalAmount
        // are already kept in sync via callbacks from InstallmentPayment
        if (splitType === 'recipients') {
          const hasValidRecipients = installmentData.some(item => item.recipient && (item.recipient.email || item.recipient.phone));
          setIsMultipleRecipients(hasValidRecipients && installmentData.length > 1);
        } else {
          setIsMultipleRecipients(false);
        }
      } else {
        // Reset installment data for other payment types
        setInstallmentData([]);
        setIsMultipleRecipients(false);
        setSplitType(null);
        setSupplierData([]);
      }
      // Show share options modal
      setShowShareModal(true);
    }
  };
  const handleViewBatchDetail = (batchId: string) => {
    navigate(`/bulk-payment-detail/${batchId}`);
  };
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
  };
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
  };
  const handleSubscriptionModeChange = (mode: SubscriptionMode) => {
    setSubscriptionMode(mode);
  };
  const handleSecurityTypeChange = (newSecurityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityType(newSecurityType);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Generate payment summary based on current configuration
  const getPaymentSummary = () => {
    const paymentTypeLabels = {
      standard: 'single payment',
      installment: 'split payment',
      'card-on-file': 'saved card payment',
      preauth: 'security hold',
      'shop-products': 'product payment',
      'fixed-links': 'fixed link payment',
      bulk: 'bulk payment',
      recurring: 'recurring subscription'
    };
    const securityLabels = {
      public: 'public access',
      password: 'password protection',
      email: 'email verification',
      sms: 'SMS verification',
      document: 'document verification',
      geo: 'geographic restriction'
    };
    let summary = `Creating a ${paymentTypeLabels[paymentType]}`;
    if (amount) {
      summary += ` for ${currency} ${amount}`;
    }
    if (reference) {
      summary += ` with reference "${reference}"`;
    }
    if (securityType !== 'public') {
      summary += ` and ${securityLabels[securityType]} security`;
    }
    if (expiryDate && paymentType !== 'card-on-file' && paymentType !== 'preauth' && paymentType !== 'fixed-links' && paymentType !== 'recurring') {
      summary += ` that expires on ${expiryDate}`;
    }
    if (paymentType === 'fixed-links') {
      summary += ` as a static reusable link to collect multiple payments`;
    }
    if (paymentType === 'recurring') {
      summary += ` that bills automatically on a recurring basis`;
    }
    return summary;
  };
  return <div className="space-y-6" data-id="element-8438" data-mp="37o0mo1lkvmjw">
      {/* Payment type selector */}
      <PaymentTypeSelector activeType={paymentType} onChange={setPaymentType} data-id="element-8439" data-mp="37qdc0zto0z6z" />
      <div data-id="element-8440" data-mp="3vewj5c0zb5xh">
        {paymentType === 'standard' && <StandardPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSecurityTypeChange={handleSecurityTypeChange} data-id="element-8441" data-mp="483c3rji8cl1" />}
        {paymentType === 'installment' && <InstallmentPayment onExpiryDateChange={handleExpiryDateChange} onSplitTypeChange={handleSplitTypeChange} onInstallmentsChange={handleInstallmentsChange} onSuppliersChange={handleSuppliersChange} onBeneficiaryTypeChange={handleBeneficiaryTypeChange} onTotalPaymentAmountChange={handleTotalPaymentAmountChange} data-id="element-8442" data-mp="myb8lqsp84s9" />}
        {paymentType === 'card-on-file' && <CardOnFilePayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSuccess={() => setShowSuccessModal(true)} data-id="element-8443" data-mp="palukhjoukl2" />}
        {paymentType === 'preauth' && <PreAuthPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-8444" data-mp="5no7canbtse9" />}
        {paymentType === 'shop-products' && <ShopProductsPayment onExpiryDateChange={handleExpiryDateChange} onCreateLink={handleButtonClick} data-id="element-8445" data-mp="396kcaz102lkf" />}
        {paymentType === 'fixed-links' && <FixedLinksPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-8446" data-mp="3j5t4bmcb1q45" />}
        {paymentType === 'recurring' && <RecurringPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSubscriptionModeChange={handleSubscriptionModeChange} data-id="element-8447" data-mp="3s453icr8lw87" />}
        {paymentType === 'bulk' && <div className="space-y-5" data-id="element-8448" data-mp="tdbj73pqvwo">
            {/* Upload Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8449" data-mp="eruu6qerhi7q">
              <div className="p-6" data-id="element-8450" data-mp="aqhtsvufu787">
                <BulkPaymentUpload simplified={true} onBatchCreated={() => {}} data-id="element-8451" data-mp="andh1a2wq35x" />
              </div>
            </div>

            {/* Recent Batches */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8452" data-mp="mpepnlud5a8f">
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100" data-id="element-8453" data-mp="rorda8t26lc2">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-8454" data-mp="wjnh067nqx50">
                  {t('components.PaymentForm.recent_batches')}
                </h3>
                <button onClick={() => navigate('/bulk-payments')} className="text-xs font-medium text-[#9F140F] hover:text-[#5C0C08] transition-colors" data-id="element-8455" data-mp="15h7j1hdcgxgo">
                  {t('components.PaymentForm.view_all')}
                </button>
              </div>
              <div className="px-6 py-2" data-id="element-8456" data-mp="3ca08lxdkv9ul">
                <BulkPaymentListSimplified onViewDetail={handleViewBatchDetail} data-id="element-8457" data-mp="3cb1m08i2g8fh" />
              </div>
            </div>
          </div>}
      </div>
      <div className={`flex items-center justify-between pt-2 ${paymentType === 'card-on-file' || paymentType === 'shop-products' ? 'hidden' : ''}`} data-id="element-8458" data-mp="1yeru40jowzh7">
        <div className="flex items-center text-sm text-gray-500" data-id="element-8459" data-mp="1pleqa3k38dgc">
          <InfoIcon size={14} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-8460" data-mp="1kliped6ot250" />
          <span data-id="element-8461" data-mp="1kjfq12jn9wnm">
            {paymentType === 'preauth' ? 'The customer will see a temporary authorization on their card' : paymentType === 'bulk' ? 'Create multiple payment requests at once by uploading a CSV file' : paymentType === 'recurring' ? subscriptionMode === 'existing' ? 'Set up automatic recurring payments for an existing customer' : 'Send a subscription link to start a recurring payment' : getPaymentSummary()}
          </span>
        </div>
        {paymentType !== 'bulk' && paymentType !== 'card-on-file' && <button className="flex-shrink-0 ml-4 px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" onClick={handleButtonClick} data-id="element-8462" data-mp="2frq2uwpex04r">
            {paymentType === 'preauth' ? 'Create pre-authorization' : paymentType === 'recurring' ? subscriptionMode === 'existing' ? 'Create subscription' : 'Create subscription link' : 'Create payment link'}
          </button>}
      </div>
      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} splitType={splitType} recipients={isMultipleRecipients ? installmentData.map(item => item.recipient).filter((recipient): recipient is Recipient => Boolean(recipient && (recipient.email || recipient.phone))) : []} installments={splitType === 'dates' ? installmentData : []} suppliers={splitType === 'commission' ? supplierData : []} totalAmount={splitType === 'commission' ? commissionTotalAmount : '0'} commissionType={commissionType} data-id="element-8463" data-mp="2plgm9n6omkdl" />}
      {/* Success Modal for Card on File and Existing Customer Subscription */}
      {showSuccessModal && <SuccessModal onClose={() => setShowSuccessModal(false)} title={paymentType === 'recurring' ? 'Subscription Created' : 'Payment Processed'} message={paymentType === 'recurring' ? 'The subscription has been successfully created and the customer will be billed according to the schedule.' : "The customer's card has been successfully charged."} data-id="element-8464" data-mp="nugh2deivao1" />}
    </div>;
}